import { ZodError, type ZodType, type ZodTypeDef } from "zod";

import type { Result } from "~/tools/result";

/**
 * Parses a JSON string and returns either an Error or { unsafe: TUnsafe } object.
 * The reason it is unsafe is because the JSON.parse function can return any type,
 * so assuming the type is correct can lead to runtime errors.
 * If you need a known type, you can pass a Zod schema as the second argument.
 *
 * @template TUnsafe - The type of the parsed object. Defaults to `unknown`.
 * @param {string} json - The JSON string to parse.
 */
export function fromJSON<TUnsafe = unknown>(json: string): Result<Error, { unsafe: TUnsafe }>;

/**
 * Parses a JSON string and validates it against a given Zod schema.
 *
 * @template TOutput - The expected output type after parsing and validation.
 * @param {string} json - The JSON string to be parsed.
 * @param {ZodType<TOutput>} schema - The Zod schema to validate the parsed JSON against.
 */
export function fromJSON<TOutput>(
	json: string,
	schema: ZodType<TOutput, ZodTypeDef, unknown>,
): Result<ZodError<TOutput>, TOutput>;

export function fromJSON<TOutput = unknown>(
	json: string,
	schema?: ZodType<TOutput>,
): Result<Error | ZodError<TOutput>, { unsafe: unknown } | TOutput> {
	return schema == null ? unknownFromJSON(json) : knownFromJson(json, schema);
}

function unknownFromJSON(json: string): Result<Error, { unsafe: unknown }> {
	try {
		return [null, { unsafe: JSON.parse(json) }];
	} catch (error) {
		return [error instanceof Error ? error : new Error("Unexpected Error during JSON.parse"), null];
	}
}

function knownFromJson<TOutput>(json: string, schema: ZodType<TOutput>): Result<ZodError<TOutput>, TOutput> {
	const [error, data] = unknownFromJSON(json);

	if (error != null) {
		return [
			new ZodError([
				{
					code: "custom",
					path: [],
					message: error.message,
				},
			]),
			null,
		];
	}

	const result = schema.safeParse(data.unsafe);

	if (result.success) {
		return [null, result.data];
	}

	return [result.error, null];
}
